import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  TrendingUp,
  Ship,
  FileText,
  Wallet,
  ShoppingCart,
  CalendarDays,
  Users,
  Building2,
  LogOut,
  Boxes,
  ChevronDown,
  Table2,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Estado de Resultados", icon: LayoutDashboard, end: true, module: "dashboard" },
  { to: "/costo-proveedores", label: "Costo Proveedores", icon: Table2, module: "costo_proveedores" },
  { to: "/precios", label: "Precios Mont Belvieu", icon: TrendingUp, module: "precios" },
  { to: "/aduanas", label: "Agencias Aduanales", icon: Ship, module: "aduanas" },
  { to: "/inspectores", label: "Inspectores / Facturas", icon: FileText, module: "inspectores" },
  { to: "/presupuesto", label: "Presupuesto", icon: Wallet, module: "presupuesto" },
  { to: "/compras", label: "Compras", icon: ShoppingCart, module: "compras" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, module: "calendario" },
];

const TITLES = {
  "/": "Estado de Resultados",
  "/precios": "Captura de Precios Mont Belvieu",
  "/aduanas": "Agencias Aduanales",
  "/inspectores": "Inspectores y Facturas",
  "/presupuesto": "Presupuesto Mensual",
  "/costo-proveedores": "Reporte Costo Proveedores",
  "/compras": "Compras",
  "/calendario": "Calendario",
  "/usuarios": "Administración de Usuarios",
  "/empresas": "Catálogo de Empresas",
};

const roleLabel = {
  superadmin: "Super Admin",
  company_admin: "Admin de Empresa",
  editor: "Edición",
  viewer: "Visualización",
};

export default function Layout() {
  const { user, logout, isSuperadmin, isManager, companies, activeCompanyId, selectCompany, activeRole, can } = useAuth();
  const location = useLocation();
  const title = TITLES[location.pathname] || "Panel";
  const visibleNav = NAV.filter((item) => can(item.module, "view"));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col h-screen fixed top-0 left-0 z-20">
        <div className="px-5 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-emerald-500/90 flex items-center justify-center">
              <Boxes className="h-5 w-5 text-slate-900" strokeWidth={2.2} />
            </div>
            <div>
              <p className="font-heading font-extrabold text-white leading-tight tracking-tight">
                ZAE Logística
              </p>
              <p className="text-[11px] text-slate-400 uppercase tracking-[0.18em]">Control financiero</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.to === "/" ? "dashboard" : item.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <item.icon className="h-5 w-5" strokeWidth={1.8} />
              {item.label}
            </NavLink>
          ))}
          {isManager && (
            <NavLink
              to="/usuarios"
              data-testid="nav-usuarios"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <Users className="h-5 w-5" strokeWidth={1.8} />
              Usuarios
            </NavLink>
          )}
          {isSuperadmin && (
            <NavLink
              to="/empresas"
              data-testid="nav-empresas"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-800 text-white" : "hover:bg-slate-800/60 hover:text-white"
                }`
              }
            >
              <Building2 className="h-5 w-5" strokeWidth={1.8} />
              Empresas
            </NavLink>
          )}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-semibold">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400">{roleLabel[activeRole] || (isSuperadmin ? "Super Admin" : "—")}</p>
            </div>
          </div>
          <button
            data-testid="btn-logout"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="ml-64">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-10">
          <h1 className="font-heading text-xl font-bold tracking-tight text-slate-900" data-testid="page-title">
            {title}
          </h1>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-400" />
            {companies.length > 0 ? (
              <div className="relative">
                <select
                  data-testid="company-selector"
                  value={activeCompanyId || ""}
                  onChange={(e) => selectCompany(e.target.value)}
                  className="appearance-none rounded-md border border-slate-300 bg-white pl-3 pr-9 py-2 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer"
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre} {c.zona ? `· ${c.zona}` : ""}
                    </option>
                  ))}
                </select>
                <ChevronDown className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            ) : (
              <span className="text-sm text-slate-400">Sin empresas asignadas</span>
            )}
          </div>
        </header>
        <main className="p-6 md:p-8">
          {activeCompanyId ? (
            <div key={activeCompanyId}>
              <Outlet />
            </div>
          ) : (
            <div className="text-center py-20 text-slate-400" data-testid="no-company">
              No tienes ninguna empresa seleccionada o asignada. Contacta a tu administrador.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
